<template>
	<span class="rx-badge" :class="`rx-badge--${TONE[status]}`">
		<Text tag="span" font="--text-10-12-bold" color="inherit">{{ LABEL[status] }}</Text>
	</span>
</template>

<script setup lang="ts">
// The /status "Rx Status" pill (Figma `kova cancel flow1`, node 30320:19121).
//
// Colour is carried by the wrapper, not the DS `Text`, because `text-transform` and
// `letter-spacing` are inherited properties — setting them on the pill styles the label
// without reaching into DS internals with `:deep()`. `Text` writes its `color` prop as an
// inline style (default `--text-dark-primary`), which would outrank the inherited tone, so
// it is explicitly handed `inherit` — keeping one source of truth per tone class below.
import { Text } from '@simple-life-app/growth-ui-kova'
import type { RxStatus } from '@/api/medBilling'

defineProps<{ status: RxStatus }>()

// Rendered uppercase by CSS (matching the design), so these stay sentence-case in source.
const LABEL: Record<RxStatus, string> = {
	in_review: 'In review',
	approved: 'Approved',
	rejected: 'Rejected',
}

const TONE: Record<RxStatus, 'warning' | 'success' | 'error'> = {
	in_review: 'warning',
	approved: 'success',
	rejected: 'error',
}
</script>

<style scoped>
/*
  Figma uses the Kova-web `--status/*` + `--brand/protein/*` ramps, which this app's design
  system (`growth-ui-kova`) doesn't ship. Each tone therefore maps to the nearest existing
  accent pair — tinted surface + saturated text, same visual family, a few units off the
  Figma hex (e.g. warning: #fff6ec/#ee8834 vs Figma #ffeccf/#c28451). Project tokens win over
  literal hexes per docs/architecture/styles.md; revisit if those ramps land in the DS.
*/
.rx-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: var(--size-4);
	padding: 6px var(--size-12);
	border-radius: var(--size-96);
	text-transform: uppercase;
	letter-spacing: -0.1px;
	white-space: nowrap;
}

.rx-badge--warning {
	background: var(--background-orange-low);
	color: var(--orange-500);
}

.rx-badge--success {
	background: var(--background-green-low);
	color: var(--green-500);
}

.rx-badge--error {
	background: var(--background-rose-low);
	color: var(--rose-500);
}
</style>
